import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ADMIN_API } from "../../../common/common";
import { Logout } from "../../../common/Funcation";
import { postApi } from "../../../service";

interface RunRow {
  run: string | number;
  amount: number;
}

function formatAmount(value: number) {
  const num = Number(value) || 0;
  const abs = Math.abs(num).toFixed(2);
  return num < 0 ? `(-${abs})` : abs;
}

function amountClass(value: number) {
  const num = Number(value) || 0;
  if (num > 0) return "is-plus";
  if (num < 0) return "is-minus";
  return "";
}

function RiskFancyRunPositionPopup() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const mode = searchParams.get("mode") === "premium" ? "premium" : "fancy";
  const matchId = searchParams.get("matchId") || "";
  const selection = searchParams.get("selection") || "";
  const subSelection = searchParams.get("subSelection") || "";
  const eventNameParam = searchParams.get("eventName") || "";
  const marketNameParam = searchParams.get("marketName") || "";

  const [eventName, setEventName] = useState(eventNameParam);
  const [marketName, setMarketName] = useState(
    marketNameParam || subSelection || selection
  );
  const [runs, setRuns] = useState<RunRow[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    document.title = "Run Position";
  }, []);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      if (!matchId || !selection) {
        setRuns([]);
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        const response = await postApi({
          api:
            mode === "premium"
              ? ADMIN_API.RISK_PREMIUM_BOOK_POSITION
              : ADMIN_API.RISK_FANCY_RUN_POSITION,
          value:
            mode === "premium"
              ? { matchId, selection }
              : {
                matchId,
                selection,
                subSelection: subSelection || undefined,
              },
        });
        const data = response.data.data || {};
        if (!cancelled) {
          setEventName(data.eventName || eventNameParam);
          setMarketName(
            data.marketName || marketNameParam || subSelection || selection
          );
          setRuns(Array.isArray(data.runs) ? data.runs : []);
        }
      } catch (err: any) {
        if (!cancelled) setRuns([]);
        if (err?.response?.data?.statusCode === 401) {
          Logout();
          navigate("/login");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [
    mode,
    matchId,
    selection,
    subSelection,
    eventNameParam,
    marketNameParam,
    navigate,
  ]);

  const closeWindow = () => {
    window.close();
  };

  return (
    <div className="risk-fancy-run-page-wrap">
      <div className="risk-fancy-run-page">
        <div className="risk-fancy-run-head">
          <div className="risk-fancy-run-title">
            <h5>{eventName || (mode === "premium" ? "Premium Market" : "Fancy Market")}</h5>
            {marketName ? (
              <span className="risk-fancy-run-pill">{marketName}</span>
            ) : null}
          </div>
          <button
            type="button"
            className="risk-fancy-run-close"
            onClick={closeWindow}
          >
            <span aria-hidden="true">×</span> Close
          </button>
        </div>

        <div className="risk-fancy-run-body">
          <h6 className="risk-fancy-run-section-title">Run Position</h6>
          <table className="risk-fancy-run-table" style={{ boxShadow: "0 2px 3px rgba(0, 0, 0, 0.3)" }}>
            <thead>
              <tr>
                <th className="align-L">Run</th>
                <th className="align-R">Amount</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={2} className="align-C">
                    Loading...
                  </td>
                </tr>
              ) : runs.length === 0 ? (
                <tr>
                  <td colSpan={2} className="align-C">
                    No Data
                  </td>
                </tr>
              ) : (
                runs.map((row) => (
                  <tr key={String(row.run)} className={amountClass(row.amount)}>
                    <td className="align-L">{row.run}</td>
                    <td className={`align-R ${amountClass(row.amount)}`}>
                      {formatAmount(row.amount)}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default RiskFancyRunPositionPopup;

function openPopupWindow(url: string, windowName: string) {
  const width = 550;
  const height = 860;
  const left = window.screenX || window.screenLeft || 0;
  const top = window.screenY || window.screenTop || 0;
  const features = [
    `width=${width}`,
    `height=${height}`,
    `left=${left}`,
    `top=${top}`,
    "resizable=yes",
    "scrollbars=yes",
    "toolbar=no",
    "menubar=no",
    "location=no",
    "status=no",
  ].join(",");

  const popup = window.open(url, windowName, features);
  if (popup) popup.focus();
  return popup;
}

export function openRiskFancyRunPositionWindow(options: {
  matchId: string;
  selection: string;
  subSelection?: string;
  eventName?: string;
  marketName?: string;
}) {
  const {
    matchId,
    selection,
    subSelection = "",
    eventName = "",
    marketName = "",
  } = options;

  const params = new URLSearchParams({
    matchId: String(matchId),
    selection: String(selection),
  });
  if (subSelection) params.set("subSelection", subSelection);
  if (eventName) params.set("eventName", eventName);
  if (marketName) params.set("marketName", marketName);

  const url = `/risk-fancy-run-position?${params.toString()}`;
  return openPopupWindow(
    url,
    `RiskFancyRunPosition_${matchId}_${selection}`
  );
}

export function openRiskPremiumBookWindow(options: {
  matchId: string;
  selection: string;
  eventName?: string;
  marketName?: string;
}) {
  const {
    matchId,
    selection,
    eventName = "",
    marketName = "",
  } = options;

  const params = new URLSearchParams({
    mode: "premium",
    matchId: String(matchId),
    selection: String(selection),
  });
  if (eventName) params.set("eventName", eventName);
  if (marketName) params.set("marketName", marketName || selection);

  const url = `/risk-fancy-run-position?${params.toString()}`;
  return openPopupWindow(
    url,
    `RiskPremiumBook_${matchId}_${selection}`
  );
}
